import bindAllOfThis from "src/utils/BindThisHelper";
import TranslateApi from "src/utils/TranslateHelper";
import { Color } from "src/games/shared/ColorValue";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import AudioTranslations from "src/constants/AudioTranslations";
import { TextBoxConfig, GameIntroTextBox } from "src/games/shared/GameIntroTextBox";
import { Games, Game } from "src/constants/Games";
import { Planets } from "src/constants/Planets";
import ConfigUtil from "src/utils/ConfigUtil";


export class GameIntroBaseScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;

    skyColor: number = 0x6eb8e5;
    index: number;
    box: TiledGameObject;
    textBox: GameIntroTextBox;

    // JB: This is so hacky it physically pains me, but it works
    public static StartFromEnd: boolean = false;

    // To be filled by child class
    introTextKeys: string[] = [];
    howToPlayScene: typeof Phaser.Scene = null;
    gameRef: Game = Games.Default;

    constructor() {
        super({
            key: "GameIntroScene"
        });
        bindAllOfThis(this, GameIntroBaseScene.prototype);
    }

    preload(): void {
        // This is a "generic" base class and we should sanity check the required conditions are met before proceeding
        if (this.gameRef === Games.Default) { throw new Error("GameIntroBaseScene: GameRef has not been initialized"); }
        if (this.howToPlayScene == null) { throw new Error("GameIntroBaseScene: HowToPlayScene has not been initialized"); }
        if (this.introTextKeys.length < 1) { throw new Error("GameIntroBaseScene: IntroTextKeys have not been initialized"); }

        // Catch load errors here
        this.load.on("loaderror", (file: Phaser.Loader.File) => {
            console.error(`File load error: ${file.key}`);
            // Audio file missed, load blank audio as it *should* always exist
            if (file.type === "audio") {
                this.load.audio(file.key, require("/src/assets/qci/sounds/blank_audio.mp3"));
            }
        });

        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.tilemapTiledJSON('outro-map', require("/src/assets/qci/maps/outro_scene.json"));
        // Loading only the needed terrain
        switch (this.gameRef.planet) {
            case Planets.R: {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/r.png"));
                break;
            }
            case Planets.I: {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/i.png"));
                break;
            }
            case Planets.A: {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/a.png"));
                break;
            }
            case Planets.S: {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/s.png"));
                break;
            }
            case Planets.E: {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/e.png"));
                break;
            }
            case Planets.C: {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/c.png"));
                break;
            }
        }
        // Load the NPC
        // JB: I wanted this to be offset for grades next to each other, so 1R would be different from 2R, like an offset matrix
        let mustard = (this.gameRef.grade.level + this.gameRef.planet.order) % 3; // 3 is how many aliens we have
        switch (mustard) {
            case 0:
                this.load.image("npc", require("/src/assets/qci/sprites/aliens/sleb-normal.png"));
                break;
            case 1:
                this.load.image("npc", require("/src/assets/qci/sprites/aliens/squeelo-normal.png"));
                break;
            case 2:
            default:
                this.load.image("npc", require("/src/assets/qci/sprites/aliens/snib-normal.png"));
                break;
        }
        this.introTextKeys.forEach((key) => {
            this.load.audio(key, AudioTranslations.FindByKeyAndLanguage(key, TranslateApi.currentLanguage.code).url);
        });
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;

        this.map = this.make.tilemap({ key: "outro-map" });

        if (GameIntroBaseScene.StartFromEnd) {
            this.index = this.introTextKeys.length - 1;
        }
        else {
            this.index = 0;
        }

        // Sound management
        this.runSoundEngine();

        let sky = this.add.image(0, 0, "pixel");
        sky.setOrigin(0);
        sky.setDisplaySize(this.gameWidth, this.gameHeight);
        sky.setTint(this.skyColor, this.skyColor, 0xffffff, 0xffffff); // Gradient sky!

        this.map.getObjectLayer("Objects").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "text") {
                this.box = ob;
                var config = {
                    padding: 20,
                    fontSize: 20,
                    hollandCode: this.gameRef.planet.name.toLowerCase(),
                    translationKey: this.introTextKeys[this.index],
                    onBack: this.back,
                    onNext: this.next,
                } as TextBoxConfig;

                this.textBox = new GameIntroTextBox(this, ob, config);
                if (GameIntroBaseScene.StartFromEnd) {
                    this.textBox.backButton.setVisible(true);
                    this.textBox.nextButton.getBackground().setTint(Color.KUDER_SECONDARY);
                }
            }
            else if (ob.name === "npc") {
                var npc = this.add.image(ob.x, ob.y, "npc");
                npc.setOrigin(0);
                npc.setDisplaySize(ob.width, ob.height);
                npc.setDepth(3);
            }
            else if (ob.name === "terrain") {
                let terrain = this.add.image(ob.x, ob.y, "terrain");
                terrain.setScale(0.8);
                terrain.setDepth(0);
                terrain.setOrigin(0);
            }
        });

        // Reset the static StartFromEnd because it's not being nice
        GameIntroBaseScene.StartFromEnd = false;
    }

    runSoundEngine(): void {
        this.sound.stopAll();
        if (!ConfigUtil.AudioEnabled) {
            return;
        }

        this.sound.add(this.introTextKeys[this.index]).play();
    }

    next(): void {
        this.sound.stopAll();
        if (this.index === this.introTextKeys.length - 1) {
            this.scene.remove(this);
            this.scene.add("HowToPlayScene", this.howToPlayScene, true);
        }
        else {
            this.index++;
            this.updateTextBox();
        }
    }

    back(): void {
        if (this.index === 0) {
            return;
        }
        else {
            this.sound.stopAll();
            this.index--;
            this.updateTextBox();
        }
    }

    updateTextBox() {
        this.textBox.destroy();
        var config = {
            padding: 20,
            fontSize: 20,
            hollandCode: this.gameRef.planet.name.toLowerCase(),
            translationKey: this.introTextKeys[this.index],
            onBack: this.back,
            onNext: this.next,
        } as TextBoxConfig;

        this.textBox = new GameIntroTextBox(this, this.box, config);

        this.textBox.backButton.visible = this.index > 0;
        if (this.textBox.backButton.visible) {
            this.textBox.nextButton.getBackground().setTint(Color.KUDER_SECONDARY_BUTTON);
        }

        // Sound management
        this.runSoundEngine();
    }
}



// WEBPACK FOOTER //
// ./src/games/shared/scenes/GameIntroBase.ts